// ✅ FULL WORKING SETUP (2 files)
// 1) Print Page Component: LubeConsumptionAlert_Print.jsx
// 2) Your List Page addition: handlePrintReport + button
//
// NOTE:
// - Your pasted code had a stray `|` after the component and the handlePrintReport was outside the component.
// - Also you used different param names in print page (branch vs branchId etc). I normalized it.
// - Print page calls /api/lubeConsumptionAlert/list with the SAME query params you pass from list page.
// - Route path uses: /vehicle/LubeConsumptionAlert_Print  (as you used in navigate)

/* =========================================================
   1) PRINT PAGE: src/pages/vehicle/LubeConsumptionAlert_Print.jsx
   ========================================================= */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaPrint, FaSyncAlt } from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/lubeConsumptionAlert";

const toStr = (v, fallback = "") =>
  v === null || v === undefined || v === "" ? fallback : String(v);

const safe = (v, fallback = "-") => {
  const s = toStr(v, "");
  return s.trim() === "" ? fallback : s;
};

export default function LubeConsumptionAlert_Print() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [header, setHeader] = useState(null); // optional if backend returns
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");

  // ✅ Read params from URL (exactly what you pass from list page)
  const params = useMemo(() => {
    return {
      // print wants all rows
      page: toStr(sp.get("page"), "1"),
      pageSize: toStr(sp.get("pageSize"), "10000"),

      vehtype: toStr(sp.get("vehtype"), "O"), // O/H
      branchId: toStr(sp.get("branchId"), "Select"),
      monthText: toStr(sp.get("monthText"), "September"),
      year: toStr(sp.get("year"), "2025"),

      vehregno: toStr(sp.get("vehregno"), "%"),
      vehname: toStr(sp.get("vehname"), "%"),
      vehcode: toStr(sp.get("vehcode"), "%"),
      vehmaintype: toStr(sp.get("vehmaintype"), "%"),

      itemCategory: toStr(sp.get("itemCategory"), "%"),
      itemName: toStr(sp.get("itemName"), "%"),

      // OPTIONAL: if you want to pass these later for rpt mapping
      fld_vehiclemaintypeid: toStr(sp.get("fld_vehiclemaintypeid"), "%"),
      fld_itemcategory: toStr(sp.get("fld_itemcategory"), "%"),
      fld_itemname: toStr(sp.get("fld_itemname"), "%"),
      totaldays: toStr(sp.get("totaldays"), "0"),
      fld_itemid: toStr(sp.get("fld_itemid"), "%"),
      columncount: toStr(sp.get("columncount"), ""),
    };
  }, [sp]);

  const fetchData = async () => {
    try {
      setError("");
      setLoading(true);

      // ✅ call backend list api with filters
      const res = await axios.get(`${API_BASE}/list`, { params });

      if (!res.data?.success) throw new Error(res.data?.message || "Failed to load report");

      setHeader(res.data.header || null);
      setRows(res.data.rows || []);
      setTotal(res.data.total ?? (res.data.rows?.length || 0));
    } catch (e) {
      setError(e?.message || "Error");
      setHeader(null);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPrint = () => window.print();

  const title = header?.title || "Lube Consumption Alert Report";
  const period = header?.period || `${params.monthText}-${params.year}`;
  const vehicleTypeText = params.vehtype === "H" ? "Hire Vehicle" : "Own Vehicle";
  const generatedOn = new Date().toLocaleString("en-IN");

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      {/* ✅ Print styles */}
      <style>{`
        @page { margin: 10mm; }
        body { margin: 0; }

        @media print {
          .no-print { display: none !important; }
          .page { padding: 0 !important; }
          .card { box-shadow: none !important; border: none !important; border-radius: 0 !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
        }
      `}</style>

      {/* ✅ Action Bar */}
      <div
        className="no-print"
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "1px solid #eee",
          position: "sticky",
          top: 0,
          background: "#fff",
          zIndex: 5,
        }}
      >
        <button onClick={() => navigate(-1)} style={btnStyle}>
          <FaArrowLeft /> Back
        </button>

        <button
          onClick={onPrint}
          disabled={loading || rows.length === 0}
          style={{
            ...btnStyle,
            background: loading || rows.length === 0 ? "#999" : "#111",
            borderColor: loading || rows.length === 0 ? "#999" : "#111",
            color: "#fff",
          }}
        >
          <FaPrint /> Print
        </button>

        <button onClick={fetchData} disabled={loading} style={btnStyle}>
          <FaSyncAlt /> Refresh
        </button>

        <div style={{ marginLeft: "auto", fontSize: 13, color: "#666" }}>
          {loading ? "Loading..." : rows.length ? `${rows.length} rows` : ""}
        </div>
      </div>

      {/* ✅ Page Content */}
      <div className="page" style={{ padding: 18 }}>
        <div
          className="card"
          style={{
            border: "1px solid #eee",
            borderRadius: 10,
            padding: 18,
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          {/* Header */}
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{title}</div>
              <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
                Period: <b>{period}</b>
              </div>
              <div style={{ fontSize: 12, color: "#777", marginTop: 2 }}>
                Generated On: <b>{generatedOn}</b>
              </div>
            </div>

            <div style={{ textAlign: "right", fontSize: 12, color: "#555" }}>
              <div>
                Vehicle Type: <b>{vehicleTypeText}</b>
              </div>
              <div>
                Branch: <b>{safe(params.branchId)}</b>
              </div>
              <div>
                Total: <b>{total}</b>
              </div>
            </div>
          </div>

          <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "14px 0" }} />

          {/* Filter summary */}
          <div
            style={{
              fontSize: 12,
              color: "#444",
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 10,
            }}
          >
            <div>Reg No: <b>{safe(params.vehregno)}</b></div>
            <div>Veh Code: <b>{safe(params.vehcode)}</b></div>
            <div>Veh Name: <b>{safe(params.vehname)}</b></div>
            <div>Main Type: <b>{safe(params.vehmaintype)}</b></div>
            <div>Item Category: <b>{safe(params.itemCategory)}</b></div>
            <div>Item Name: <b>{safe(params.itemName)}</b></div>
          </div>

          <div style={{ marginTop: 14 }}>
            {error ? (
              <div
                style={{
                  padding: 12,
                  background: "#fff3f3",
                  border: "1px solid #ffd0d0",
                  borderRadius: 8,
                  color: "#a40000",
                }}
              >
                {error}
              </div>
            ) : !loading && rows.length === 0 ? (
              <div style={{ padding: 14, background: "#fafafa", border: "1px solid #eee", borderRadius: 8 }}>
                No records found
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      <Th style={{ width: 55, textAlign: "center" }}>S.No</Th>
                      <Th>Branch</Th>
                      <Th>Vehicle RegNo</Th>
                      <Th>Vehicle Name</Th>
                      <Th>Vehicle Code</Th>
                      <Th>Vehicle Main Type</Th>
                      <Th>Item Category</Th>
                      <Th>Item Name</Th>
                      <Th style={{ textAlign: "right" }}>Current Level</Th>
                      <Th style={{ textAlign: "right" }}>Threshold</Th>
                      <Th>Status</Th>
                      <Th>Last Refill</Th>
                      <Th>Consumption Rate</Th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.id || `${r.vehicleRegNo}-${r.itemName}-${i}`}>
                        <Td style={{ textAlign: "center" }}>{i + 1}</Td>
                        <Td>{safe(r.branch ?? r.fld_branchname)}</Td>
                        <Td>{safe(r.vehicleRegNo ?? r.fld_vehregno)}</Td>
                        <Td>{safe(r.vehicleName ?? r.fld_vehicle)}</Td>
                        <Td>{safe(r.vehicleCode ?? r.fld_vehcode)}</Td>
                        <Td>{safe(r.vehicleMainType ?? r.fld_vehiclemaintype)}</Td>
                        <Td>{safe(r.itemCategory ?? r.fld_itemcategory)}</Td>
                        <Td>{safe(r.itemName ?? r.fld_itemname)}</Td>
                        <Td style={{ textAlign: "right" }}>{safe(r.currentLevel ?? r.fld_current)}</Td>
                        <Td style={{ textAlign: "right" }}>{safe(r.threshold ?? r.fld_threshold)}</Td>
                        <Td>{safe(r.status ?? r.fld_status)}</Td>
                        <Td>{safe(r.lastRefill ?? r.fld_lastrefill)}</Td>
                        <Td>{safe(r.consumptionRate ?? r.fld_consumptionrate)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ marginTop: 16, fontSize: 11, color: "#777" }}>
            Note: This is a system generated report.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- UI helpers ---------------- */

const btnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  border: "1px solid #ddd",
  padding: "8px 12px",
  borderRadius: 8,
  background: "#fff",
  cursor: "pointer",
  fontWeight: 600,
};

function Th({ children, style }) {
  return (
    <th
      style={{
        borderBottom: "1px solid #ddd",
        textAlign: "left",
        padding: "10px 8px",
        background: "#fafafa",
        fontWeight: 800,
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, style }) {
  return (
    <td
      style={{
        borderBottom: "1px solid #f0f0f0",
        padding: "9px 8px",
        verticalAlign: "top",
        ...style,
      }}
    >
      {children}
    </td>
  );
}

/* =========================================================
   2) LIST PAGE: Add handlePrintReport + Button
   Put inside your LubeConsumptionAlert component
   ========================================================= */

/*
import { createSearchParams } from "react-router-dom";
import { FaPrint } from "react-icons/fa";

const handlePrintReport = () => {
  const vehtype = vehicleType === "own" ? "O" : "H";

  const query = {
    vehtype,
    branchId: filters.branch || "Select",
    monthText: filters.month,
    year: filters.year,

    vehregno: filters.vehicleRegNo ? `${filters.vehicleRegNo}%` : "%",
    vehname: filters.vehicleName ? `${filters.vehicleName}%` : "%",
    vehcode: filters.vehicleCode ? `${filters.vehicleCode}%` : "%",
    vehmaintype: filters.vehicleMainType || "%",

    itemCategory: filters.itemCategory || "%",
    itemName: filters.itemName || "%",

    // print wants all rows
    page: "1",
    pageSize: "10000",
  };

  navigate({
    pathname: "/vehicle/LubeConsumptionAlert_Print",
    search: `?${createSearchParams(query).toString()}`,
  });
};

<button
  onClick={handlePrintReport}
  className="flex items-center gap-2 bg-black hover:bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
>
  <FaPrint />
  Print Report
</button>
*/

/* =========================================================
   3) ROUTE ADD
   ========================================================= */

/*
import LubeConsumptionAlert_Print from "../pages/vehicle/LubeConsumptionAlert_Print";

<Route path="/vehicle/LubeConsumptionAlert_Print" element={<LubeConsumptionAlert_Print />} />
*/